// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { isEqual, omit } from 'lodash';

// consts/enums
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import {
    APPLY_ROLL_TO_OPTIONS,
    APPLY_ROLL_TO_OPTIONS_FAILURE,
    APPLY_ROLL_TO_OPTIONS_PAYMENT_FAILED,
    CLEAR_ROLL_TO_OPTIONS,
    RESET_ROLL_TO_OPTIONS,
    REVERT_ROLL_TO_ORIGINAL,
    ROLL_TO_PAYMENT_TOGGLE,
    SET_ROLL_TO_ACTIVE_TAB,
    UPDATE_ROLL_ADJUST,
    UPDATE_ROLL_TARGET_BALANCE_DUE,
    UPDATE_ROLL_TARGET_CASH_DUE,
    UPDATE_ROLL_TARGET_PAYMENT,
    UPDATE_ROLL_TARGET_PROFIT,
    UPDATE_ROLL_TO_OPTIONS
} from '../actionTypes/rollToOptionsActionTypes';

import type { RollToActionValues } from '@makemydeal/dr-dash-types';
import type { RequestRollToOptionsPayload } from '../reducers/rollToOptionsReducer';

import type { StateTree } from '@makemydeal/dr-dash-types';
import { paymentActionTypes, paymentSelectors } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors, type ReduxNext, type ReduxStore } from '@makemydeal/dr-shared-store';
import { ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import * as rollToActionCreators from '../actions/rollToActionCreators';
import * as offerSelectors from '../selectors/offerSelectors';
import * as compositeSelectors from '../selectors/compositeSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';

import { getIncludedTradeInItems } from '../selectors/tradeIns';
import { handleRollToPayment } from '../utils/rollToPaymentDispatcher';
import {
    cancelPendingRollToUpqSwitch,
    discardCancelledRollToUpqSwitchResponse,
    endRollToSession,
    peekEndpointBeforeRollTo,
    recordEndpointBeforeRollTo,
    recordRollToUpqSwitchRequest,
    setRollToApplying,
    settleRollToUpqSwitch,
    takeEndpointBeforeRollTo
} from '../utils/rollToEndpointUtils';
import { BALLOON, type RollToOption } from '@makemydeal/dr-platform-types';
import * as rollToSelectors from '../selectors/rollToSelectors';
import { CALC_ENDPOINTS } from '../constants';
import { updateCurrentCalculationEndpoint } from '../actions/offerRedux';

const { PAYMENT_ABORTED, PAYMENT_REQUESTED } = paymentActionTypes;

const revertToEndpointBeforeRollTo = (store: ReduxStore<StateTree>, state: StateTree) => {
    const before = takeEndpointBeforeRollTo(state);
    const isMovedFromProgramQuotes =
        before?.endpoint === CALC_ENDPOINTS.programQuotes &&
        offerReduxSelectors.getCalculationEndpointByOfferType(state, before.offerType) === CALC_ENDPOINTS.userProgramQuotes;
    if (!before || !isMovedFromProgramQuotes) return;

    cancelPendingRollToUpqSwitch(state);
    store.dispatch(
        updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, before.offerType, {
            withPayment: before.offerType !== BALLOON
        })
    );
};

export const middleware = (store: ReduxStore<StateTree>) => (next: ReduxNext) => async (action: AnyFSA) => {
    // Capture state BEFORE reducer runs for SET_ROLL_TO_ACTIVE_TAB comparison
    let shouldSkipRollToApiCalls = false;
    if (action.type === SET_ROLL_TO_ACTIVE_TAB && action.meta?.middleware?.rollTo === true) {
        const preState = store.getState();
        const newTab = action.payload as ROLL_TO_OPTIONS_LABELS;
        const rootAdjust = rollToSelectors.getRollToOptions(preState).rollToItems?.adjust;
        const tabAdjust = rollToSelectors.getTabState(preState, newTab).rollToItems?.adjust;

        // Compare adjust values excluding actionValue (which is tab-specific)
        const rootAdjustWithoutActionValue = omit(rootAdjust || {}, ['actionValue']);
        const tabAdjustWithoutActionValue = omit(tabAdjust || {}, ['actionValue']);

        shouldSkipRollToApiCalls = isEqual(rootAdjustWithoutActionValue, tabAdjustWithoutActionValue);
    }

    const isSwitchResponse = action.type === PAYMENT_RECEIVED || action.type === PAYMENT_FAILURE || action.type === PAYMENT_ABORTED;
    if (isSwitchResponse && discardCancelledRollToUpqSwitchResponse(action) && action.type !== PAYMENT_ABORTED) {
        return;
    }

    const wasSlideOutExpanded =
        action.type === ROLL_TO_PAYMENT_TOGGLE && rollToSelectors.getRollToPaymentSlideOutExpanded(store.getState());

    next(action);

    const state = store.getState();

    if (action.type === PAYMENT_REQUESTED) {
        recordRollToUpqSwitchRequest(action);
    }

    if (action.type === PAYMENT_RECEIVED || action.type === PAYMENT_FAILURE || action.type === PAYMENT_ABORTED) {
        settleRollToUpqSwitch(action);
    }

    if (action.type === CLEAR_ROLL_TO_OPTIONS || action.type === RESET_ROLL_TO_OPTIONS) {
        endRollToSession(state);
    }

    switch (action.type) {
        case UPDATE_ROLL_TARGET_BALANCE_DUE:
        case UPDATE_ROLL_TARGET_CASH_DUE:
        case UPDATE_ROLL_TARGET_PROFIT:
        case UPDATE_ROLL_TARGET_PAYMENT: {
            const term = offerReduxSelectors.getSelectedTermMonths(state);
            const balloonAmount = offerReduxSelectors.getBalloonAmount(state);
            const rate = featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled(state)
                ? offerReduxSelectors.getSellRate(state)
                : offerSelectors.getSellRateOverrideWithFallback(state);
            const downPayment = offerSelectors.getDownPayment(state);
            const sellingPrice = offerSelectors.getSellingPrice(state);
            const taxesAndFees = compositeSelectors.taxesAndFeesTotal(state);
            const addOns = compositeSelectors.getTotalAddons(state);
            const payment = offerSelectors.getMonthlyPayment(state);
            const profit = offerReduxSelectors.getSelectedPaymentProfitDetails(state);
            const balanceDue = offerSelectors.getBalanceDueAmountByOffer(state);
            const cashDue = offerReduxSelectors.getCashDueFromCustomer(state);
            const allTradeIns = getIncludedTradeInItems(state);
            const tradeIns: RollToOption[] = allTradeIns.map((tradeIn) => ({
                value: tradeIn.value || 0,
                adjusted: false,
                callerItemId: tradeIn.callerItemId
            }));

            const targetMetaByActionType: Record<string, { actionValue: number; adjustmentType: ROLL_TO_OPTIONS_LABELS }> = {
                [UPDATE_ROLL_TARGET_PAYMENT]: { actionValue: payment, adjustmentType: ROLL_TO_OPTIONS_LABELS.PAYMENT },
                [UPDATE_ROLL_TARGET_BALANCE_DUE]: { actionValue: balanceDue, adjustmentType: ROLL_TO_OPTIONS_LABELS.BALANCE_DUE },
                [UPDATE_ROLL_TARGET_CASH_DUE]: { actionValue: cashDue, adjustmentType: ROLL_TO_OPTIONS_LABELS.CASH_DUE },
                [UPDATE_ROLL_TARGET_PROFIT]: {
                    actionValue: profit?.frontEndProfit || 0,
                    adjustmentType: ROLL_TO_OPTIONS_LABELS.PROFIT
                }
            };
            const { actionValue, adjustmentType } = targetMetaByActionType[action.type];

            const current: RollToActionValues = {
                addOns,
                downPayment,
                actionValue,
                rate,
                sellingPrice,
                term,
                balloonAmount,
                taxesAndFees,
                tradeIns,
                adjustmentType
            };

            const payload: RequestRollToOptionsPayload = {
                rollToItems: {
                    current,
                    adjust: current,
                    profit
                },
                balanceDue,
                cashDue,
                payment
            };
            store.dispatch({
                type: UPDATE_ROLL_TO_OPTIONS,
                payload
            });

            break;
        }
        case UPDATE_ROLL_ADJUST: {
            await handleRollToPayment({
                state,
                action,
                store,
                optionType: 'adjust'
            });
            break;
        }
        case ROLL_TO_PAYMENT_TOGGLE: {
            if (featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled(state)) {
                if (!wasSlideOutExpanded) {
                    recordEndpointBeforeRollTo(state);
                } else if (!peekEndpointBeforeRollTo(state)?.applying) {
                    revertToEndpointBeforeRollTo(store, state);
                }
            }
            store.dispatch({
                type: CLEAR_ROLL_TO_OPTIONS
            });
            !featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled(state) &&
                offerReduxSelectors.getCalculationEndpointByOfferType(state, offerReduxSelectors.getCurrentOfferType(state)) !==
                    CALC_ENDPOINTS.userProgramQuotes &&
                store.dispatch(
                    updateCurrentCalculationEndpoint(
                        CALC_ENDPOINTS.userProgramQuotes,
                        offerReduxSelectors.getCurrentOfferType(state),
                        {
                            withPayment: true
                        }
                    )
                );
            break;
        }

        case APPLY_ROLL_TO_OPTIONS: {
            if (featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled(state)) setRollToApplying(state, true);
            break;
        }

        case APPLY_ROLL_TO_OPTIONS_FAILURE: {
            if (featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled(state)) {
                setRollToApplying(state, false);
                if (!rollToSelectors.getRollToPaymentSlideOutExpanded(state)) revertToEndpointBeforeRollTo(store, state);
            }
            break;
        }

        case REVERT_ROLL_TO_ORIGINAL: {
            await handleRollToPayment({
                state,
                action,
                store,
                optionType: 'current'
            });
            break;
        }

        case SET_ROLL_TO_ACTIVE_TAB: {
            // Trigger UPR and UPQ calls when switching to a tab with a target value
            if (action.meta?.middleware?.rollTo === true && !shouldSkipRollToApiCalls) {
                await handleRollToPayment({
                    state,
                    action,
                    store,
                    optionType: 'adjust'
                });
            }
            break;
        }

        case PAYMENT_RECEIVED: {
            if (action.payload.actionForPayment === APPLY_ROLL_TO_OPTIONS) {
                if (paymentSelectors.getIsError(state)) {
                    const messages = paymentSelectors.getErrorMessages(state);
                    store.dispatch(rollToActionCreators.applyRollToOptionsError(action, messages));
                } else {
                    store.dispatch(rollToActionCreators.applyRollToOptionsSuccess(action));
                }
            }
            break;
        }

        case PAYMENT_FAILURE: {
            if (action.meta?.actionForPayment === APPLY_ROLL_TO_OPTIONS) {
                store.dispatch(rollToActionCreators.applyRollToOptionsError(action));
            }
            break;
        }

        case APPLY_ROLL_TO_OPTIONS_PAYMENT_FAILED: {
            store.dispatch(rollToActionCreators.applyRollToOptionsError(action));
            break;
        }

        default:
            break;
    }
};
